import { type AppActionState, appActionStateSchema } from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import { toast } from '@crowdex/ui/components/toaster';
import { type ReactNode, useEffect, useState } from 'react';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import type { AppAction } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { callApp } from './apps-client';

/**
 * Where an action runs: the conversation whose workspace its requests reach (never shown to the
 * plugin) and what the host can do there.
 */
export type AppActionHost = {
  conversation?: string | undefined;
  /** The artifact an artifact action runs on: `{title}` and `{path}` in its text. */
  artifact?: { title: string; path: string } | undefined;
  /** The widget a card action runs on: a `call` gets `{ widget }`, `{title}` is its title. */
  widget?: { ref: Record<string, unknown>; title: string } | undefined;
  ask(text: string): void;
  open(
    action: AppAction & { run: Extract<AppAction['run'], { open: string }> },
  ): unknown | Promise<unknown>;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Runs an action, after the confirmation it declares. */
export async function runAppAction(action: AppAction, host: AppActionHost): Promise<void> {
  if (action.confirm !== undefined && !window.confirm(action.confirm)) return;
  const { run } = action;
  const fill = (text: string) =>
    host.artifact !== undefined
      ? text.replaceAll('{title}', host.artifact.title).replaceAll('{path}', host.artifact.path)
      : host.widget !== undefined
        ? text.replaceAll('{title}', host.widget.title)
        : text;
  if ('ask' in run) return host.ask(fill(run.ask));
  if ('open' in run) {
    await host.open({ ...action, run });
    return;
  }
  await callApp({
    plugin: action.plugin,
    route: run.call,
    method: 'POST',
    // A card's action gets its widget, an artifact's the artifact it runs on.
    ...(host.widget !== undefined
      ? { input: { widget: host.widget.ref } }
      : host.artifact !== undefined
        ? { input: { artifact: { title: host.artifact.title, path: host.artifact.path } } }
        : {}),
    ...(host.conversation !== undefined && UUID.test(host.conversation)
      ? { conversation: host.conversation }
      : {}),
  });
}

/** An action's state from its `state` route, refreshed when the conversation changes. */
function useActionState(action: AppAction, conversation: string | undefined, revision: unknown) {
  const [state, setState] = useState<AppActionState>({});
  useEffect(() => {
    void revision;
    if (action.state === undefined) return;
    let live = true;
    callApp({
      plugin: action.plugin,
      route: action.state,
      method: 'GET',
      ...(conversation !== undefined && UUID.test(conversation) ? { conversation } : {}),
    }).then(
      (value) => {
        const parsed = appActionStateSchema.safeParse(value);
        if (live && parsed.success) setState(parsed.data);
      },
      () => {
        if (live) setState({ disabled: true });
      },
    );
    return () => {
      live = false;
    };
  }, [action.plugin, action.state, conversation, revision]);
  return state;
}

function AppActionButton({
  action,
  host,
  revision,
}: {
  action: AppAction;
  host: AppActionHost;
  revision: unknown;
}) {
  const [runs, setRuns] = useState(0);
  // Its state again after it ran (a dialog changed what it shows).
  const state = useActionState(action, host.conversation, `${String(revision)}:${runs}`);
  const [running, setRunning] = useState(false);
  if (state.hidden) return null;
  const label = state.label ?? action.label;
  const Icon = pluginIcon(state.icon ?? action.icon ?? 'app');
  return (
    <Button
      aria-label={label}
      aria-pressed={state.active}
      disabled={state.disabled === true || running}
      onClick={() => {
        setRunning(true);
        runAppAction(action, host)
          .catch((error: unknown) =>
            toast.error(
              `${action.pluginTitle}: ${label}`,
              error instanceof Error ? error.message : String(error),
            ),
          )
          .finally(() => {
            setRunning(false);
            setRuns((value) => value + 1);
          });
      }}
      size="sm"
      title={`${label} (${action.pluginTitle})`}
      type="button"
      variant={state.active ? 'secondary' : 'ghost'}
    >
      <Icon className="size-4" />
      <span className="sr-only">{label}</span>
    </Button>
  );
}

/** The composer actions of the account's apps. */
export function AppComposerActions({ busy, host }: { busy: boolean; host: AppActionHost }) {
  const workbench = useWorkbench();
  const actions = workbench.actions.filter((action) => action.where === 'composer');
  return (
    <>
      {actions.map((action) => (
        <AppActionButton
          action={action}
          host={host}
          key={`${action.plugin}/${action.id}`}
          revision={busy}
        />
      ))}
    </>
  );
}

/**
 * The actions under a widget's card, labelled and quiet: what the core offers first (`children`:
 * an artifact's Open), the apps' card actions, and, for an artifact, its type's own actions.
 */
export function WidgetCardActions({
  host,
  artifact,
  children,
}: {
  host: AppActionHost;
  /** The artifact the card shows: its plugin and type pick its actions. */
  artifact?: { plugin: string; type: string } | undefined;
  children?: ReactNode;
}) {
  const workbench = useWorkbench();
  const actions = workbench.actions.filter(
    (action) =>
      action.where === 'card' ||
      (artifact !== undefined &&
        action.where === 'artifact' &&
        action.plugin === artifact.plugin &&
        (action.when?.types === undefined || action.when.types.includes(artifact.type))),
  );
  if (actions.length === 0 && children === undefined) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {children}
      {actions.map((action) => (
        <AppActionLabelButton action={action} host={host} key={`${action.plugin}/${action.id}`} />
      ))}
    </div>
  );
}

/**
 * An action as a labelled button, with its `state` (label, icon, active, disabled, hidden),
 * refreshed after it runs: under a widget's card, and on an artifact's header.
 */
export function AppActionLabelButton({
  action,
  host,
  size = 'xs',
  variant = 'ghost',
}: {
  action: AppAction;
  host: AppActionHost;
  size?: 'xs' | 'sm';
  variant?: 'ghost' | 'secondary' | 'default';
}) {
  const [runs, setRuns] = useState(0);
  // Its state again after it ran (it may now be active, disabled or relabelled).
  const state = useActionState(action, host.conversation, `${host.widget?.title ?? ''}:${runs}`);
  const [running, setRunning] = useState(false);
  if (state.hidden) return null;
  const label = state.label ?? action.label;
  const Icon = pluginIcon(state.icon ?? action.icon ?? 'app');
  return (
    <Button
      disabled={state.disabled === true || running}
      onClick={() => {
        setRunning(true);
        runAppAction(action, host)
          // An ask shows in the conversation; a call says it was done.
          .then(() => 'call' in action.run && toast.success(`${action.pluginTitle}: ${label}`))
          .catch((error: unknown) =>
            toast.error(
              `${action.pluginTitle}: ${label}`,
              error instanceof Error ? error.message : String(error),
            ),
          )
          .finally(() => {
            setRunning(false);
            setRuns((value) => value + 1);
          });
      }}
      size={size}
      title={`${label} (${action.pluginTitle})`}
      type="button"
      variant={variant}
    >
      <Icon />
      {label}
    </Button>
  );
}
